// The admin ledger's "Last 24 hours / 7 days" pulse (owner request
// 2026-09-25: "a quick glance on what has been happening over the past day —
// who transacted and who didn't, how much, and whether that is their usual
// level — with the focus on catching anything of concern").
//
// Pure logic, unit-tested under node (tests/investment-v2/activity-pulse.test.mjs).
//
// What counts:
//   · TRANSACTING = every ledger row that is not pool funding and not Kareem
//     moving pool money onward. Pending draws count (the draw happened).
//   · FUNDING = a row landing on a pool destination (Kareem Trust / Kraken /
//     the gold + invest bridges). Investors do NOT fund daily (owner, same
//     day), so funding is reported as a pool figure, never as "less than
//     they drew today".
//   · USUAL = the household's own last 28 days before the window (4 weeks in
//     the 7-day view): the middle half of its ACTIVE days is the usual range,
//     the median is the typical day, and how many days were active says
//     whether silence is unusual.
//
// Flags (the concern list and each household's chip):
//   red    pool      household pool position below zero (v2_pool_position)
//   amber  poolSoon  still ≥ 0, but its pending draws take it below zero
//   amber  spike     transacted ≥ 2× its typical day/week
//   amber  low       transacted, but < half its typical day/week
//   amber  quiet     nothing in the window from a household active on ≥ 4 of
//                    7 days (day view) / ≥ 3 of 4 weeks (week view)
//   info   new       transacted with no activity in the baseline
//   info   idle      nothing for 30+ days
// Plus one cross-household item: movements awaiting Kareem for over a day.

import { dayOf, isoDay } from './activityGroups.js';

export const POOL_IN_KINDS = new Set(['kareem_trust', 'kareem_kraken', 'kareem_gold_bridge', 'kareem_invest_bridge']);

export const isFunding = (r) => POOL_IN_KINDS.has(r.dstKind);
const isPlumbing = (r) => typeof r.srcKind === 'string' && r.srcKind.startsWith('kareem_');
export const isTransaction = (r) => !isFunding(r) && !isPlumbing(r);

const DAY = 86400000;
const hasTime = (iso) => String(iso || '').length > 10;
const dayStart = (d) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());

// A value event carries a timestamp; a value-less movement only a date. A
// date-only row is in the window when its day is on/after the window's first
// day (so "24 hours" reads "today and yesterday" for those).
export function inWindow(r, startMs, endMs) {
  const iso = r.eventDate;
  if (!iso) return false;
  if (hasTime(iso)) { const t = Date.parse(iso); return t >= startMs && t <= endMs; }
  const d = dayOf(iso);
  return d >= isoDay(new Date(startMs)) && d <= isoDay(new Date(endMs));
}

export function quantile(sorted, q) {
  if (!sorted.length) return 0;
  const pos = (sorted.length - 1) * q;
  const lo = Math.floor(pos), hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

// Bucket a household's transacting rows by UTC day key.
function dailyTotals(rows) {
  const by = {};
  rows.forEach((r) => { const d = dayOf(r.eventDate); by[d] = (by[d] || 0) + Math.abs(Number(r.amount || 0)); });
  return by;
}

// Sum of `by` over the n-day span ending on (and including) `endDay` ms.
function spanSum(by, endDayMs, n) {
  let s = 0;
  for (let i = 0; i < n; i++) s += by[isoDay(new Date(endDayMs - i * DAY))] || 0;
  return s;
}

// mode: 'day' (last 24h vs the prior 28 days) | 'week' (last 7 days vs the
// prior 4 weeks). pool: { household → { available, pending_out } } or null.
// enrolled: the investment households (module_enrollment, enabled). When
// given, it is the whole universe: a member who is not an investor (a gold
// player, a buying-group member) never appears, and rows or pool entries of
// anyone else are ignored. When null, households come from the rows.
export function buildPulse({ rows: allRows, enrolled = null, pool: allPool = null, now = new Date(), mode = 'day' }) {
  const only = Array.isArray(enrolled) ? new Set(enrolled) : null;
  // The pulse reads money MOVEMENTS. Cash settlement rows (payments to
  // Kareem, payouts, waivers — in the ledger since 2026-10-23) are records of
  // settling up, not transactions, so they stay out of every pulse figure.
  const movementRows = (allRows || []).filter((r) => !r.settleType);
  const rows = only ? movementRows.filter((r) => only.has(r.household || r.member)) : movementRows;
  const pool = allPool && only ? Object.fromEntries(Object.entries(allPool).filter(([h]) => only.has(h))) : allPool;
  const week = mode === 'week';
  const span = week ? 7 : 1;                    // days per bucket
  const nBase = week ? 4 : 28;                   // baseline buckets
  const quietMin = week ? 3 : 16;                // active buckets for "quiet" (≈ 4 of 7 days)
  const endMs = now.getTime();
  const startMs = endMs - span * DAY;
  const today = dayStart(now);
  // The baseline ends the day before the window's first day.
  const baseEnd = dayStart(new Date(startMs)) - DAY;

  const tx = rows.filter(isTransaction);
  const inWin = (r) => inWindow(r, startMs, endMs);

  // Per-household rows.
  const byHh = {};
  const hhIds = new Set(only || []);
  tx.forEach((r) => { const h = r.household || r.member; if (!h) return; hhIds.add(h); (byHh[h] = byHh[h] || []).push(r); });
  Object.keys(pool || {}).forEach((h) => hhIds.add(h));

  const out = [...hhIds].map((id) => {
    const own = byHh[id] || [];
    const win = own.filter(inWin);
    const amount = win.reduce((s, r) => s + Math.abs(Number(r.amount || 0)), 0);
    const value = win.reduce((s, r) => s + Number(r.value || 0), 0);
    const daily = dailyTotals(own);
    const base = [];
    for (let i = 0; i < nBase; i++) base.push(spanSum(daily, baseEnd - i * span * DAY, span));
    const active = base.filter((v) => v > 0).sort((a, b) => a - b);
    const typical = quantile(active, 0.5);
    const lo = quantile(active, 0.25);
    const hi = quantile(active, 0.75);
    // Sparkline: 14 days (day view) / 12 weeks (week view), oldest first,
    // the last bar being the current day / week.
    const nSpark = week ? 12 : 14;
    const spark = [];
    for (let i = nSpark - 1; i >= 0; i--) spark.push(spanSum(daily, today - i * span * DAY, span));
    const lastIso = own.reduce((m, r) => (String(r.eventDate || '') > m ? String(r.eventDate) : m), '');
    const lastActive = lastIso ? dayOf(lastIso) : null;
    const daysSince = lastActive ? Math.floor((today - Date.parse(lastActive + 'T00:00:00Z')) / DAY) : null;

    const p = pool && pool[id] ? { available: Number(pool[id].available || 0), pendingOut: Number(pool[id].pending_out || 0) } : null;
    const flags = [];
    if (p && p.available < 0) flags.push({ kind: 'pool', level: 'red', label: 'Pool below zero' });
    else if (p && p.pendingOut > 0 && p.available - p.pendingOut < 0) flags.push({ kind: 'poolSoon', level: 'amber', label: 'Pending draws go negative' });
    if (amount > 0 && typical > 0 && amount >= 2 * typical) flags.push({ kind: 'spike', level: 'amber', label: 'Above usual', ratio: amount / typical });
    else if (amount > 0 && typical > 0 && amount < 0.5 * typical) flags.push({ kind: 'low', level: 'amber', label: 'Below usual', ratio: amount / typical });
    else if (amount > 0 && typical === 0) flags.push({ kind: 'new', level: 'info', label: 'First in ' + (week ? '4 weeks' : '28 days') });
    if (amount === 0 && active.length >= quietMin) flags.push({ kind: 'quiet', level: 'amber', label: 'Quiet' });
    else if (amount === 0 && (daysSince === null || daysSince >= 30)) flags.push({ kind: 'idle', level: 'info', label: daysSince === null ? 'No activity yet' : 'Idle 30+ days' });

    return { id, amount, value, count: win.length, typical, lo, hi, activeBuckets: active.length, baseBuckets: nBase, spark, lastActive, daysSince, pool: p, flags };
  });

  const rank = (h) => (h.flags.some((f) => f.level === 'red') ? 0 : h.flags.some((f) => f.level === 'amber') ? 1 : h.amount > 0 ? 2 : 3);
  out.sort((a, b) => rank(a) - rank(b) || b.amount - a.amount || String(a.id).localeCompare(String(b.id)));

  // Headline numbers.
  const winTx = tx.filter(inWin);
  const moved = winTx.reduce((s, r) => s + Math.abs(Number(r.amount || 0)), 0);
  const allDaily = dailyTotals(tx);
  const baseAll = [];
  for (let i = 0; i < nBase; i++) baseAll.push(spanSum(allDaily, baseEnd - i * span * DAY, span));
  const usualMoved = quantile(baseAll.filter((v) => v > 0).sort((a, b) => a - b), 0.5);
  const funded = rows.filter((r) => isFunding(r) && inWin(r)).reduce((s, r) => s + Math.abs(Number(r.amount || 0)), 0);

  // Waiting on Kareem for more than a day (any kind of movement).
  const dayAgo = endMs - DAY;
  const waitingRows = rows.filter((r) => r.movementStatus === 'pending' && (hasTime(r.eventDate) ? Date.parse(r.eventDate) < dayAgo : dayOf(r.eventDate) < isoDay(new Date(dayAgo))));

  return {
    mode, startMs, endMs,
    households: out,
    kpis: {
      moved,
      usualMoved,
      value: winTx.reduce((s, r) => s + Number(r.value || 0), 0),
      kareem: winTx.reduce((s, r) => s + Number(r.kareem || 0), 0),
      investor: winTx.reduce((s, r) => s + Number(r.investor || 0), 0),
      active: out.filter((h) => h.amount > 0).length,
      total: out.length,
      quiet: out.filter((h) => h.flags.some((f) => f.kind === 'quiet')).length,
      funded,
      belowZero: out.filter((h) => h.pool && h.pool.available < 0).length,
    },
    waiting: { count: waitingRows.length, amount: waitingRows.reduce((s, r) => s + Math.abs(Number(r.amount || 0)), 0) },
  };
}

// The concern list: one sentence per red/amber flag, red first, then the
// waiting-on-Kareem line. name(id) → display name; fmt(n) → money string.
export function concernsOf(pulse, name, fmt) {
  const per = pulse.mode === 'week' ? 'week' : 'day';
  const span = pulse.mode === 'week' ? 'this week' : 'in the last 24 hours';
  const items = [];
  pulse.households.forEach((h) => {
    h.flags.forEach((f) => {
      const n = name(h.id);
      if (f.kind === 'pool') items.push({ level: 'red', kind: 'pool', id: h.id, text: `${n}'s pool balance is ${fmt(h.pool.available)}` + (h.pool.pendingOut > 0 ? `, with ${fmt(h.pool.pendingOut)} more in pending draws` : '') });
      if (f.kind === 'poolSoon') items.push({ level: 'amber', kind: 'poolSoon', id: h.id, text: `${n}'s pending draws (${fmt(h.pool.pendingOut)}) would take their pool balance of ${fmt(h.pool.available)} below zero` });
      if (f.kind === 'spike') items.push({ level: 'amber', kind: 'spike', id: h.id, text: `${n} moved ${fmt(h.amount)} ${span}, ${f.ratio.toFixed(1)}× their typical ${per} (${fmt(h.typical)})` });
      if (f.kind === 'low') items.push({ level: 'amber', kind: 'low', id: h.id, text: `${n} moved ${fmt(h.amount)} ${span}, ${Math.round((1 - f.ratio) * 100)}% below their typical ${per} (${fmt(h.typical)})` });
      if (f.kind === 'quiet') items.push({ level: 'amber', kind: 'quiet', id: h.id, text: `${n} hasn't transacted ${span}` + (h.daysSince != null ? ` (last on ${h.lastActive})` : '') + ` — usually active ${h.activeBuckets} of ${h.baseBuckets} ${per}s` });
    });
  });
  items.sort((a, b) => (a.level === b.level ? 0 : a.level === 'red' ? -1 : 1));
  if (pulse.waiting.count > 0) {
    items.push({ level: 'amber', kind: 'waiting', id: null, text: `${pulse.waiting.count} movement${pulse.waiting.count === 1 ? ' has' : 's have'} been awaiting you for more than a day (${fmt(pulse.waiting.amount)})` });
  }
  return items;
}
